type Rgba = { r: number; g: number; b: number; a: number }

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

export function parseColor(color: string): Rgba {
  const value = color.trim()

  const hex = value.match(/^#([0-9a-f]{3,8})$/i)
  if (hex) {
    let digits = hex[1]
    if (digits.length === 3 || digits.length === 4) {
      digits = digits
        .split('')
        .map((digit) => digit + digit)
        .join('')
    }
    if (digits.length !== 6 && digits.length !== 8) {
      throw new Error(`Invalid color: ${color}`)
    }
    return {
      r: parseInt(digits.slice(0, 2), 16),
      g: parseInt(digits.slice(2, 4), 16),
      b: parseInt(digits.slice(4, 6), 16),
      a: digits.length === 8 ? parseInt(digits.slice(6, 8), 16) / 255 : 1,
    }
  }

  const rgb = value.match(/^rgba?\(([^)]+)\)$/i)
  if (rgb) {
    const [r, g, b, a = '1'] = rgb[1].split(',').map((part) => part.trim())
    return { r: Number(r), g: Number(g), b: Number(b), a: Number(a) }
  }

  throw new Error(`Invalid color: ${color}`)
}

const toHexPair = (value: number) =>
  Math.round(clamp(value, 0, 255))
    .toString(16)
    .padStart(2, '0')
    .toUpperCase()

export function withAlpha(color: string, alpha: number): string {
  const { r, g, b } = parseColor(color)
  return `rgba(${r}, ${g}, ${b}, ${clamp(alpha, 0, 1)})`
}

/** Mistura `from` em direção a `to`; `amount` 0 devolve `from`, 1 devolve `to`. */
export function mix(from: string, to: string, amount: number): string {
  const a = parseColor(from)
  const b = parseColor(to)
  const t = clamp(amount, 0, 1)
  const channel = (x: number, y: number) => x + (y - x) * t

  return `#${toHexPair(channel(a.r, b.r))}${toHexPair(
    channel(a.g, b.g),
  )}${toHexPair(channel(a.b, b.b))}`
}

export function relativeLuminance(color: string): number {
  const { r, g, b } = parseColor(color)
  const linear = (channel: number) => {
    const value = channel / 255
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  }

  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b)
}

export function contrastRatio(first: string, second: string): number {
  const a = relativeLuminance(first)
  const b = relativeLuminance(second)
  const [light, dark] = a > b ? [a, b] : [b, a]

  return (light + 0.05) / (dark + 0.05)
}

/** Entre os candidatos, o que tem maior contraste com o fundo. */
export function pickReadable(background: string, candidates: string[]): string {
  return candidates.reduce((best, candidate) =>
    contrastRatio(background, candidate) > contrastRatio(background, best)
      ? candidate
      : best,
  )
}

export function isDarkColor(color: string): boolean {
  return relativeLuminance(color) < 0.18
}
